import { rm, combineReducers } from 'utils';
import { BaseAction, baseFormReducer, BaseInitialState, baseReducer, BaseState } from '../Utils';
import {
  ImageAction,
  ImageInitialState,
  imagesReducer,
  ImageState
} from '../Components/ImageUpload/ImageHelper';
import { SupportProductModel } from 'models/SupportProductModel';
import { isCustomerContactValid } from '../Components/CustomerContact/utils';

type InternalState = BaseState &
  ImageState & {
    products: SupportProductModel[];
    productsError?: string;
    reason: string;
    reasonError: string;
    description: string;
    descriptionError: string;
  };

export const claimsInitialState: InternalState = {
  ...BaseInitialState,
  ...ImageInitialState,
  products: [],
  reason: '',
  reasonError: '',
  description: '',
  descriptionError: ''
};

type Action =
  | { type: 'productChanged'; value: SupportProductModel }
  | { type: 'productsError'; value: string }
  | { type: 'reason'; value: string }
  | { type: 'reasonError'; value: string }
  | { type: 'description'; value: string }
  | { type: 'descriptionError'; value: string }
  | BaseAction
  | ImageAction;

export const claimsReducer = (state: InternalState, action: Action): InternalState => {
  switch (action.type) {
    case 'reason':
      return { ...state, reason: action.value };
    case 'reasonError':
      return { ...state, reasonError: action.value };
    case 'description':
      return { ...state, description: action.value };
    case 'descriptionError':
      return { ...state, descriptionError: action.value };
    case 'productChanged':
      return { ...state, products: getProducts(action.value, state.products) };
    case 'productsError':
      return { ...state, productsError: action.value };
    default:
      return state;
  }
};

export const reducer = combineReducers<InternalState, Action>(
  baseFormReducer,
  baseReducer,
  claimsReducer,
  imagesReducer
);

const getProducts = (
  product: SupportProductModel,
  selectedProducts: SupportProductModel[]
): SupportProductModel[] => {
  if (selectedProducts.some(sp => sp.productId === product.productId)) {
    return selectedProducts.map(sp => {
      if (sp.productId === product.productId) {
        return { ...sp, quantity: product.quantity };
      }
      return sp;
    });
  }

  selectedProducts.push({
    productId: product.productId,
    productName: product.productName,
    productRecievedDate: product.productRecievedDate,
    quantity: product.quantity,
    unit: product.unit
  });

  return selectedProducts;
};

export const isValid = (
  state: InternalState,
  addressChanging: boolean,
  dispatch: (action: Action) => void
) => {
  let isValid = true;

  if (addressChanging) {
    dispatch({
      type: 'addressError',
      value: rm('customerpage_returns_validation_error_address')
    });
    isValid = false;
  }

  if (state.reason === '') {
    dispatch({
      type: 'reasonError',
      value: rm('customerpage_claims_reason_error')
    });
    isValid = false;
  }

  if (state.description === '') {
    dispatch({
      type: 'descriptionError',
      value: rm('customerpage_claims_description_error')
    });
    isValid = false;
  }

  if (state.products.filter(p => p.quantity > 0).length === 0) {
    dispatch({
      type: 'productsError',
      value: rm('customerpage_returns_validation_error_products')
    });
    isValid = false;
  }

  isValid = isCustomerContactValid({ state, valid: isValid, dispatch });

  return isValid;
};

export const productChanged = (
  product: SupportProductModel,
  dispatch: (action: Action) => void
) => {
  dispatch({ type: 'productChanged', value: product });
  dispatch({ type: 'productsError', value: '' });
};

export const reasonChange = (value: string | number, dispatch: (action: Action) => void) => {
  dispatch({
    type: 'reason',
    value: value.toString()
  });
  dispatch({
    type: 'reasonError',
    value: ''
  });
};

export const getReasonOptions = () => [
  {
    label: rm('customerservice_question_wrongdelivery'),
    value: rm('customerservice_question_wrongdelivery'),
    id: 'reason-002'
  },
  {
    label: rm('customerservice_question_qualityissue'),
    value: rm('customerservice_question_qualityissue'),
    id: 'reason-003'
  },
  {
    label: rm('customerservice_question_missingarticle'),
    value: rm('customerservice_question_missingarticle'),
    id: 'reason-004'
  }
];
